import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Badge } from "../../components/ui/badge";
import { CheckCircle2, ArrowRight, ArrowLeft, Euro, Calendar, CreditCard, Banknote, FileCheck2, Sparkles, Tag, X } from "lucide-react";
import { toast } from "sonner";

const cents = (v) => (Number(v || 0) / 100).toFixed(0) + " €";

export default function OnboardingRequestPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [cgv, setCgv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [draftToken, setDraftToken] = useState(null);

  // Code promo
  const [promoInput, setPromoInput] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount_percent, description, is_internal}
  const [promoChecking, setPromoChecking] = useState(false);

  const checkPromo = async () => {
    const c = (promoInput || "").trim().toUpperCase();
    if (!c) return;
    setPromoChecking(true);
    try {
      const { data } = await api.post("/agency/promo-codes/validate", { code: c });
      setPromoApplied(data);
      toast.success(`Code ${c} accepté · -${data.discount_percent}%`);
    } catch (e) {
      setPromoApplied(null);
      toast.error(e?.response?.data?.detail || "Code invalide");
    } finally {
      setPromoChecking(false);
    }
  };
  const removePromo = () => {
    setPromoApplied(null);
    setPromoInput("");
  };

  // Sprint F2 — Reprise d'un brouillon via ?draft=token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("draft");
    if (t) {
      api.get(`/agency/onboarding/draft/${t}`)
        .then(r => {
          const d = r.data;
          setDraftToken(t);
          setForm(prev => ({ ...prev, ...d, draft_token: undefined }));
          if (d.last_step_completed >= 1) setStep(Math.min(4, d.last_step_completed + 1));
        })
        .catch(() => {});
    }
  }, []);

  // Sprint F2 — Autosave après chaque step transition
  const saveDraft = async (currentStep) => {
    try {
      const payload = { ...form, draft_token: draftToken, last_step_completed: currentStep };
      const r = await api.post("/agency/onboarding/draft", payload);
      if (!draftToken && r.data?.draft_token) setDraftToken(r.data.draft_token);
    } catch { /* silent */ }
  };

  const [form, setForm] = useState({
    plan_code: "",
    extra_widgets: [],
    company_name: "",
    contact_first_name: "",
    contact_last_name: "",
    contact_email: "",
    contact_phone: "",
    billing_address: "",
    billing_zip: "",
    billing_city: "",
    siret: "",
    sector_preset: "",
    notes: "",
    setup_payment_method: "stripe",
    setup_installments: 1,
    cgv_accepted: false,
    signed_name: "",
  });

  useEffect(() => {
    Promise.all([
      api.get("/agency/public/plans"),
      api.get("/agency/public/widgets"),
      api.get("/agency/public/cgv/current"),
    ]).then(([p, w, c]) => {
      setPlans(p.data); setWidgets(w.data); setCgv(c.data);
      if (p.data.length > 0) setForm(f => ({ ...f, plan_code: p.data[0].code }));
      setLoading(false);
    }).catch(() => { setLoading(false); toast.error("Erreur chargement forfaits"); });
  }, []);

  const selectedPlan = plans.find(p => p.code === form.plan_code);

  const totalExtra = form.extra_widgets.reduce((sum, key) => {
    const w = widgets.find(x => x.widget_key === key);
    if (!w) return sum;
    const base = w.monthly_price_cents || 0;
    const discounted = w.discount_pct > 0 ? base * (1 - w.discount_pct / 100) : base;
    return sum + discounted;
  }, 0);

  const next = () => {
    if (step === 1 && !form.plan_code) return toast.error("Choisis un forfait");
    if (step === 2) {
      if (!form.company_name || !form.contact_email || !form.contact_first_name || !form.contact_phone) {
        return toast.error("Tous les champs obligatoires doivent être remplis");
      }
    }
    if (step === 3) {
      if (!form.cgv_accepted) return toast.error("Tu dois accepter les CGV");
      if (!form.signed_name.trim()) return toast.error("Signe en tapant ton nom");
    }
    saveDraft(step);                  // Sprint F2 : autosave à chaque transition
    setStep(step + 1);
  };
  const prev = () => setStep(Math.max(1, step - 1));

  const submit = async () => {
    if (!form.cgv_accepted || !form.signed_name.trim()) {
      toast.error("Acceptation CGV + signature requises");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        company_name: form.company_name,
        contact_first_name: form.contact_first_name,
        contact_last_name: form.contact_last_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        billing_address: form.billing_address,
        billing_zip: form.billing_zip,
        billing_city: form.billing_city,
        siret: form.siret,
        plan_code: form.plan_code,
        sector_preset: form.sector_preset,
        extra_widgets: form.extra_widgets,
        notes: form.notes,
        setup_payment_method: form.setup_payment_method,
        setup_installments: form.setup_installments,
        cgv_version: cgv.version,
        signed_at: new Date().toISOString(),
        signed_name: form.signed_name,
        draft_token: draftToken,
      };
      const r = await api.post("/agency/onboarding/submit", payload);
      let request = r.data;

      // Application du code promo si saisi
      if (promoApplied?.code) {
        try {
          const promoRes = await api.post("/agency/onboarding/apply-promo", {
            onboarding_request_id: request.id,
            code: promoApplied.code,
          });
          // Si 100% gratuit → on saute Stripe, on affiche directement la confirmation
          if (promoRes.data?.is_free) {
            request = { ...request, setup_fee_total_cents: 0, setup_payment_method: "promo_free", status: "paid_setup", promo_code_applied: promoApplied.code };
            setSubmitted(request);
            setStep(5);
            return;
          }
          // Sinon on met à jour le total local pour la suite Stripe
          request = { ...request, setup_fee_total_cents: promoRes.data.new_total_cents };
        } catch (e) {
          toast.error("Code promo : " + (e?.response?.data?.detail || e.message));
        }
      }

      // Si mode Stripe → créer une session et rediriger
      if (form.setup_payment_method === "stripe") {
        try {
          const pay = await api.post("/agency/onboarding/pay-setup", {
            onboarding_request_id: request.id,
            installment_number: 1,
            origin_url: window.location.origin,
          });
          if (pay.data?.url) {
            window.location.href = pay.data.url;
            return;
          }
          toast.error("Lien de paiement Stripe indisponible");
        } catch (e) {
          toast.error("Stripe: " + (e?.response?.data?.detail || e.message));
        }
      }

      // Virement bancaire → on affiche la confirmation directement
      setSubmitted(request);
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="p-20 text-center">Chargement…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white py-10 px-4" data-testid="onboarding-public-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <Badge className="bg-sky-500 mb-4">Demande de site</Badge>
          <h1 className="text-4xl font-bold mb-2">Lançons votre site COCKPIT</h1>
          <p className="text-slate-300">Choisissez votre forfait, signez les CGV, réglez les frais de setup. On s'occupe du reste.</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-between mb-8">
          {["Forfait", "Coordonnées", "CGV & Signature", "Paiement"].map((lbl, i) => (
            <div key={lbl} className={`flex-1 text-center ${i + 1 <= step ? "opacity-100" : "opacity-40"}`}>
              <div className={`w-10 h-10 mx-auto rounded-full flex items-center justify-center font-bold mb-2 ${i + 1 < step ? "bg-emerald-500" : i + 1 === step ? "bg-sky-500" : "bg-slate-700"}`}>
                {i + 1 < step ? <CheckCircle2 className="w-5 h-5" /> : i + 1}
              </div>
              <div className="text-xs">{lbl}</div>
            </div>
          ))}
        </div>

        <Card className="p-6 bg-slate-800 border-slate-700 text-white">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold mb-2">Étape 1 — Choisissez votre forfait</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {plans.map(p => (
                  <Card key={p.code} className={`p-4 cursor-pointer transition ${form.plan_code === p.code ? "bg-sky-500/20 border-sky-500" : "bg-slate-900 border-slate-700 hover:border-slate-500"}`} onClick={() => setForm({ ...form, plan_code: p.code })} data-testid={`plan-option-${p.code}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-lg">{p.name}</h3>
                      {form.plan_code === p.code && <CheckCircle2 className="w-5 h-5 text-sky-400" />}
                    </div>
                    <div className="text-2xl font-bold text-white mb-1">{cents(p.monthly_price_cents)}<span className="text-sm text-slate-400">/mois</span></div>
                    <div className="text-sm text-slate-400">+ setup {cents(p.setup_fee_cents)} ({p.setup_fee_installments_max}×)</div>
                    <p className="text-xs mt-3 text-slate-300">{p.description}</p>
                    <div className="mt-3 space-y-1 text-xs">
                      <div>✓ {p.included_widgets.length} widgets inclus</div>
                      {p.trial_days > 0 && <div>✓ {p.trial_days} jours d'essai</div>}
                      <div>✓ {p.ai_credits_monthly} crédits IA/mois</div>
                      <div>✓ {p.support_hours_monthly}h support/mois</div>
                    </div>
                  </Card>
                ))}
              </div>

              {widgets.filter(w => w.is_payable).length > 0 && (
                <div className="pt-4 border-t border-slate-700">
                  <h3 className="font-bold mb-2">Widgets supplémentaires (optionnel)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {widgets.filter(w => w.is_payable).map(w => {
                      const isIncluded = selectedPlan?.included_widgets?.includes(w.widget_key);
                      const checked = form.extra_widgets.includes(w.widget_key);
                      return (
                        <label key={w.widget_key} className={`flex items-start gap-2 p-3 rounded border ${isIncluded ? "bg-emerald-950/30 border-emerald-700 opacity-70" : "bg-slate-900 border-slate-700 cursor-pointer hover:border-sky-500"}`}>
                          <Checkbox checked={isIncluded || checked} disabled={isIncluded} onCheckedChange={v => {
                            if (isIncluded) return;
                            setForm({ ...form, extra_widgets: v ? [...form.extra_widgets, w.widget_key] : form.extra_widgets.filter(x => x !== w.widget_key) });
                          }} />
                          <div className="flex-1">
                            <div className="font-medium text-sm">{w.label} {isIncluded && <Badge className="bg-emerald-500 ml-1 text-xs">Inclus</Badge>}</div>
                            <div className="text-xs text-slate-400">{w.description}</div>
                            {!isIncluded && <div className="text-xs text-sky-400 mt-1">+{cents(w.monthly_price_cents)}/mois {w.discount_pct > 0 && <span className="text-red-400">(-{w.discount_pct}%)</span>}</div>}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold">Étape 2 — Vos coordonnées</h2>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Entreprise *</Label><Input className="bg-slate-900 border-slate-700" value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} data-testid="onb-company" /></div>
                <div><Label>SIRET</Label><Input className="bg-slate-900 border-slate-700" value={form.siret} onChange={e => setForm({ ...form, siret: e.target.value })} /></div>
                <div><Label>Prénom *</Label><Input className="bg-slate-900 border-slate-700" value={form.contact_first_name} onChange={e => setForm({ ...form, contact_first_name: e.target.value })} data-testid="onb-firstname" /></div>
                <div><Label>Nom</Label><Input className="bg-slate-900 border-slate-700" value={form.contact_last_name} onChange={e => setForm({ ...form, contact_last_name: e.target.value })} /></div>
                <div><Label>Email *</Label><Input className="bg-slate-900 border-slate-700" type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} data-testid="onb-email" /></div>
                <div><Label>Téléphone *</Label><Input className="bg-slate-900 border-slate-700" value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} data-testid="onb-phone" /></div>
              </div>
              <div><Label>Adresse</Label><Input className="bg-slate-900 border-slate-700" value={form.billing_address} onChange={e => setForm({ ...form, billing_address: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>CP</Label><Input className="bg-slate-900 border-slate-700" value={form.billing_zip} onChange={e => setForm({ ...form, billing_zip: e.target.value })} /></div>
                <div className="col-span-2"><Label>Ville</Label><Input className="bg-slate-900 border-slate-700" value={form.billing_city} onChange={e => setForm({ ...form, billing_city: e.target.value })} /></div>
              </div>
              <div><Label>Secteur d'activité</Label><Input className="bg-slate-900 border-slate-700" value={form.sector_preset} onChange={e => setForm({ ...form, sector_preset: e.target.value })} placeholder="boulangerie, plombier, avocat…" /></div>
              <div><Label>Remarques / attentes</Label><Textarea className="bg-slate-900 border-slate-700" rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
          )}

          {step === 3 && cgv && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold flex items-center gap-2"><FileCheck2 className="w-5 h-5" />Étape 3 — {cgv.title} ({cgv.version})</h2>
              <div className="bg-slate-900 border border-slate-700 rounded p-4 max-h-80 overflow-y-auto text-sm prose prose-invert prose-sm" dangerouslySetInnerHTML={{ __html: cgv.content_html }} />
              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={form.cgv_accepted} onCheckedChange={v => setForm({ ...form, cgv_accepted: !!v })} data-testid="onb-cgv-accept" />
                  <span className="text-sm">J'ai lu et j'accepte les Conditions Générales de Vente version <strong>{cgv.version}</strong> en vigueur.</span>
                </label>
                <div>
                  <Label>Signature (tapez votre nom complet) *</Label>
                  <Input className="bg-slate-900 border-slate-700 font-serif italic text-lg" placeholder="Jean Dupont" value={form.signed_name} onChange={e => setForm({ ...form, signed_name: e.target.value })} data-testid="onb-signature" />
                </div>
              </div>
            </div>
          )}

          {step === 4 && selectedPlan && (
            <div className="space-y-4">
              <h2 className="text-xl font-bold flex items-center gap-2"><CreditCard className="w-5 h-5" />Étape 4 — Paiement des frais de setup</h2>
              <Card className="p-4 bg-slate-900 border-slate-700">
                <h3 className="font-bold mb-2">Récapitulatif</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span>Forfait {selectedPlan.name}</span><strong>{cents(selectedPlan.monthly_price_cents)}/mois</strong></div>
                  {form.extra_widgets.length > 0 && (
                    <div className="flex justify-between text-sky-400"><span>+ {form.extra_widgets.length} widget(s) optionnels</span><strong>+{cents(totalExtra)}/mois</strong></div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-slate-700">
                    <strong>Frais de setup (aujourd'hui)</strong>
                    {promoApplied ? (
                      <div className="text-right">
                        <div className="line-through text-slate-500 text-xs">{cents(selectedPlan.setup_fee_cents)}</div>
                        <strong className="text-emerald-400">
                          {cents(Math.round(selectedPlan.setup_fee_cents * (100 - promoApplied.discount_percent) / 100))}
                          {promoApplied.discount_percent === 100 && " · GRATUIT"}
                        </strong>
                      </div>
                    ) : (
                      <strong className="text-sky-400">{cents(selectedPlan.setup_fee_cents)}</strong>
                    )}
                  </div>
                </div>
              </Card>

              {/* Code promo */}
              <Card className="p-4 bg-slate-900 border-slate-700" data-testid="promo-block">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="w-4 h-4 text-sky-400" />
                  <h3 className="font-bold text-sm">Code promo</h3>
                </div>
                {promoApplied ? (
                  <div className="flex items-center justify-between gap-2 bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3">
                    <div className="min-w-0">
                      <div className="font-mono font-bold text-emerald-400">{promoApplied.code}</div>
                      <div className="text-xs text-slate-400 truncate">
                        -{promoApplied.discount_percent}% appliqué{promoApplied.description ? ` · ${promoApplied.description}` : ""}
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={removePromo} data-testid="promo-remove" className="text-slate-400 hover:text-red-400">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      className="bg-slate-950 border-slate-700 font-mono uppercase"
                      placeholder="Ex: MSC-FREE-100"
                      value={promoInput}
                      onChange={e => setPromoInput(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === "Enter" && checkPromo()}
                      data-testid="promo-input"
                    />
                    <Button onClick={checkPromo} disabled={promoChecking || !promoInput.trim()} variant="outline" data-testid="promo-apply-btn">
                      {promoChecking ? "…" : "Appliquer"}
                    </Button>
                  </div>
                )}
              </Card>

              {/* Mode de paiement (masqué si gratuit total) */}
              {!(promoApplied && promoApplied.discount_percent === 100) && (
              <>
              <div>
                <Label>Mode de paiement</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Card className={`p-3 cursor-pointer flex items-center gap-2 ${form.setup_payment_method === "stripe" ? "bg-sky-500/20 border-sky-500" : "bg-slate-900 border-slate-700"}`} onClick={() => setForm({ ...form, setup_payment_method: "stripe" })}>
                    <CreditCard className="w-4 h-4" />Carte bancaire (Stripe)
                  </Card>
                  <Card className={`p-3 cursor-pointer flex items-center gap-2 ${form.setup_payment_method === "bank_transfer" ? "bg-sky-500/20 border-sky-500" : "bg-slate-900 border-slate-700"}`} onClick={() => setForm({ ...form, setup_payment_method: "bank_transfer" })}>
                    <Banknote className="w-4 h-4" />Virement bancaire
                  </Card>
                </div>
              </div>
              <div>
                <Label>Nombre d'échéances (max {selectedPlan.setup_fee_installments_max})</Label>
                <div className="flex gap-2 mt-2">
                  {[1, 2, 3, 4].filter(n => n <= selectedPlan.setup_fee_installments_max).map(n => (
                    <Button key={n} variant={form.setup_installments === n ? "default" : "outline"} onClick={() => setForm({ ...form, setup_installments: n })} data-testid={`onb-installments-${n}`}>
                      {n}× {cents(Math.ceil(selectedPlan.setup_fee_cents / n))}
                    </Button>
                  ))}
                </div>
              </div>
              </>
              )}
              {(promoApplied && promoApplied.discount_percent === 100) && (
                <Card className="p-4 bg-emerald-500/10 border-emerald-500/40">
                  <div className="text-sm text-emerald-300">
                    🎉 Code <strong className="font-mono">{promoApplied.code}</strong> · création <strong>gratuite</strong>. Aucun paiement n'est requis. Validez la demande pour finaliser.
                  </div>
                </Card>
              )}
            </div>
          )}

          {step === 5 && submitted && (
            <div className="text-center space-y-4 py-10">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-500 flex items-center justify-center">
                <CheckCircle2 className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-2xl font-bold">Demande enregistrée !</h2>
              <p className="text-slate-300">Ta demande a bien été envoyée. Notre équipe va te contacter sous 24h pour lancer la création de ton site.</p>
              <div className="bg-slate-900 p-4 rounded text-left max-w-md mx-auto">
                <div className="text-xs text-slate-400">ID de demande</div>
                <div className="font-mono text-xs">{submitted.id}</div>
                <div className="text-xs text-slate-400 mt-2">Forfait</div>
                <div>{submitted.plan_snapshot?.name || submitted.plan_code}</div>
                <div className="text-xs text-slate-400 mt-2">Setup à régler</div>
                <div>{cents(submitted.setup_fee_total_cents)} ({submitted.setup_installments}×, {submitted.setup_payment_method === "stripe" ? "CB" : "Virement"})</div>
              </div>
              <Button onClick={() => nav("/")}>Retour à l'accueil</Button>
            </div>
          )}

          {step < 5 && (
            <div className="flex justify-between pt-6 mt-6 border-t border-slate-700">
              <Button variant="outline" disabled={step === 1} onClick={prev} className="text-white border-slate-600"><ArrowLeft className="w-4 h-4 mr-1" />Précédent</Button>
              {step < 4 ? (
                <Button onClick={next} data-testid={`onb-next-step-${step}`}>Suivant<ArrowRight className="w-4 h-4 ml-1" /></Button>
              ) : (
                <Button onClick={submit} disabled={submitting} data-testid="onb-submit-btn">{submitting ? "Envoi…" : "Valider la demande"}<ArrowRight className="w-4 h-4 ml-1" /></Button>
              )}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
